import { useState } from 'react'
import { ChevronRight, ChevronDown, Plus, Pencil, Trash2, Users } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import type { ApiDepartment } from '@/types/department'

interface TreeNodeProps {
	node: ApiDepartment
	selectedId: number | undefined
	onSelect: (id: number) => void
	onAddChild: (parentId: number) => void
	onEdit: (dept: ApiDepartment) => void
	onDelete: (dept: ApiDepartment) => void
	level?: number
}

export function TreeNode({
	node,
	selectedId,
	onSelect,
	onAddChild,
	onEdit,
	onDelete,
	level = 0,
}: TreeNodeProps) {
	const [isExpanded, setIsExpanded] = useState(level < 2)
	const hasChildren = node.children && node.children.length > 0
	const isSelected = selectedId === node.id

	const handleToggle = (e: React.MouseEvent) => {
		e.stopPropagation()
		if (hasChildren) {
			setIsExpanded(!isExpanded)
		}
	}

	const handleSelect = () => {
		onSelect(node.id)
	}

	return (
		<div className="select-none">
			<div
				className={cn(
					'group flex items-center gap-1 px-2 py-1.5 cursor-pointer rounded-md transition-colors',
					isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
				)}
				style={{ paddingLeft: `${level * 16 + 8}px` }}
				onClick={handleSelect}
			>
				{/* 展开/折叠按钮 */}
				<button
					onClick={handleToggle}
					className={cn(
						'h-4 w-4 flex items-center justify-center shrink-0',
						!hasChildren && 'invisible'
					)}
				>
					{hasChildren && (
						isExpanded ? (
							<ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
						) : (
							<ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
						)
					)}
				</button>

				{/* 部门名称 */}
				<span className="flex-1 truncate text-sm font-medium">
					{node.name}
				</span>

				{/* 用户数量 */}
				<span className="flex items-center gap-0.5 text-xs text-muted-foreground">
					<Users className="h-3 w-3" />
					{node.user_count || 0}
				</span>

				{/* 操作按钮（悬停显示） */}
				<div className="hidden group-hover:flex items-center gap-0.5">
					<Button
						variant="ghost"
						size="icon"
						className="h-6 w-6"
						onClick={(e) => {
							e.stopPropagation()
							onAddChild(node.id)
						}}
					>
						<Plus className="h-3 w-3" />
					</Button>
					<Button
						variant="ghost"
						size="icon"
						className="h-6 w-6"
						onClick={(e) => {
							e.stopPropagation()
							onEdit(node)
						}}
					>
						<Pencil className="h-3 w-3" />
					</Button>
					<Button
						variant="ghost"
						size="icon"
						className="h-6 w-6 text-destructive hover:text-destructive"
						onClick={(e) => {
							e.stopPropagation()
							onDelete(node)
						}}
					>
						<Trash2 className="h-3 w-3" />
					</Button>
				</div>
			</div>

			{/* 子节点 */}
			{hasChildren && isExpanded && (
				<div>
					{node.children!.map((child) => (
						<TreeNode
							key={child.id}
							node={child}
							selectedId={selectedId}
							onSelect={onSelect}
							onAddChild={onAddChild}
							onEdit={onEdit}
							onDelete={onDelete}
							level={level + 1}
						/>
					))}
				</div>
			)}
		</div>
	)
}
